import React, { useState, useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { toast } from 'react-toastify';
import axios from 'axios';
import moment from 'moment';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../../src/config/firebase';
import IconButton from 'components/common/IconButton';
import PageHeader from 'components/common/PageHeader';
import FileUpload from 'components/doc-components/FileUpload';
import FalconComponentCard from 'components/common/FalconComponentCard';
import categories from '../../data/categories';
import audiences from '../../config/audiences';

const CreateDocumentUploadForm = () => {
  const navigate = useNavigate();
  const [fileUrl, setFileUrl] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: categories[0]?.dir_name || 'speakers_list',
    user_id: null,
    audience: 'public',
    enabled: true,
  });

  useEffect(() => {
    // Bypass auth for testing if environment variable is set
    if (process.env.REACT_APP_BYPASS_AUTH === 'true') {
      setFormData({
        ...formData,
        user_id: 'test-user-id',
      });
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        setFormData({
          ...formData,
          user_id: uid,
        });
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const matchedCategory = categories.filter(
        (category) => category.dir_name === formData.category
      );
      
      if (!matchedCategory || matchedCategory.length === 0) {
        toast.error('Invalid category selected', {
          theme: 'colored',
        });
        return;
      }
      
      let category_id = matchedCategory[0].category_id;
      let formData2 = new FormData();
      formData2.append('title', formData.title);
      formData2.append('category_id', category_id);
      formData2.append('category', formData.category);
      formData2.append('user_id', formData.user_id);
      formData2.append('date_updated', moment.utc().format('YYYY-MM-DD HH:mm'));
      formData2.append('date_added', moment.utc().format('YYYY-MM-DD HH:mm'));
      formData2.append('year_added', moment.utc().format('YYYY'));
      formData2.append('fileUrl', fileUrl);
      formData2.append('audience', formData.audience);
      formData2.append('enabled', formData.enabled);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/documents/create`,
        formData2
      );

      if (response) {
        // console.log('responded', response.statusText);

        toast.success(`Document uploaded successfully`, {
          theme: 'colored',
        });

        setFormData({
          ...formData,
          title: '',
          category: 'test',
        });
        setFileUrl(null);

        navigate('/documents/list');
      }
    } catch (err) {
      console.log('Error', err.message);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === 'date_added' || name === 'date_updated') {
      const dateValue = moment.utc(value).format('YYYY-MM-DD HH:mm');
      setFormData({
        ...formData,
        [name]: dateValue,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  return (
    <>
      <PageHeader
        title='Upload a document'
        description='Load pdf files for Bills, Hansard etc'
        className='mb-3'
      ></PageHeader>

      <FalconComponentCard>
        <FalconComponentCard.Header
          title='Details'
          light={false}
          noPreview={true}
        ></FalconComponentCard.Header>
        <Form onSubmit={handleSubmit}>
          <FalconComponentCard.Body>
            <Form.Label>Title</Form.Label>
            <Form.Control
              size='lg'
              type='text'
              placeholder=''
              name='title'
              onChange={handleFieldChange}
            />
            <br />

            <Row>
              <Col lg='6'>
                <Form.Group>
                  <Form.Label>Audience</Form.Label>
                  <Form.Select
                    size='md'
                    className='mb-3'
                    name='audience'
                    onChange={handleFieldChange}
                    defaultChecked={formData.audience}
                  >
                    {audiences.map((audience) => (
                      <option key={audience.value} value={audience.value}>
                        {audience.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col lg='6'>
                <Form.Group>
                  <Form.Label>Document category</Form.Label>
                  <Form.Select
                    size='md'
                    className='mb-3'
                    name='category'
                    value={formData.category}
                    onChange={handleFieldChange}
                  >
                    {categories.map((category) => (
                      <option
                        key={category.category_id}
                        value={category.dir_name}
                      >
                        {category.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <FileUpload category={formData.category} setFileUrl={setFileUrl} />

            <Row>
              <Col lg='6' className='mt-4 mb-3 ml-3'>
                <Form.Group>
                  <IconButton
                    className='mb-1'
                    variant='falcon-default'
                    size='lg'
                    icon='plus'
                    transform='shrink-3'
                    type='submit'
                    disabled={!fileUrl}
                  >
                    Submit
                  </IconButton>
                </Form.Group>
              </Col>
            </Row>
          </FalconComponentCard.Body>
        </Form>
      </FalconComponentCard>
    </>
  );
};

export default CreateDocumentUploadForm;
